import { useEffect, useContext } from "react";

import usePhotoUploadsInDropzone from "../usePhotoUploadsInDropzone";

import { generatePreviewFile, getUploadPreviewObject } from "./helpers";

import AllPhotoUploadsContext from "../../context/AllPhotoUploadsContext";

const usePhotoUploadPreviewData = () => {
    const context = useContext(AllPhotoUploadsContext);

    if (context === undefined) {
        throw new Error(
            "usePhotoUploadPreviewData must be used within an AllPhotoUploadsProvider"
        );
    }

    const { previewData, setPreviewData } = context;
    const {
        uploads,
        refetch: refetchUploadsInDropzone
    } = usePhotoUploadsInDropzone();

    const getUploadPreview = uploadId => {
        return previewData[uploadId];
    };

    useEffect(async () => {
        if (!uploads || !uploads.length) return;

        const uploadsToProcess = uploads.filter(upload => {
            let result = false;
            const uploadPreview = previewData[upload.id];

            // if preview data for upload does not yet exist
            // filter upload for processing
            if (!uploadPreview) {
                result = true;
            } else {
                // if thumbnail URL has expired and new thumbnail URL has been generated
                // filter upload for processing
                if (upload.thumbnail !== uploadPreview.thumbnail) {
                    result = true;
                }
            }

            return result;
        });

        if (!uploadsToProcess.length) return;

        const previews = await Promise.all(
            uploadsToProcess.map(async upload => {
                const uploadPreviewFile = await generatePreviewFile(
                    upload,
                    refetchUploadsInDropzone
                );
                const uploadPreviewObject = getUploadPreviewObject(
                    upload,
                    uploadPreviewFile
                );

                return uploadPreviewObject;
            })
        );

        const newPreviewData = previews.reduce(
            (data, upload) => ({
                ...data,
                [upload.id]: {
                    aspectRatio: upload.aspectRatio,
                    preview: upload.preview
                }
            }),
            {}
        );

        setPreviewData(prevPreviewData => ({
            ...prevPreviewData,
            ...newPreviewData
        }));
    }, [uploads]);

    return {
        getUploadPreview
    };
};

export default usePhotoUploadPreviewData;
